import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useStaffMe, isMutating } from '../../hooks/useStaffMe';
import { DetailPageHeader } from '../../components/DetailPageHeader';
import { useToast } from '../../components/ToastProvider';
import { ActionConfirmModal } from './ActionConfirmModal';
import { SubscriptionStateModal } from './SubscriptionStateModal';
import { AddCreditModal } from './AddCreditModal';
import {
  getCustomer,
  listCustomerAdminAudit,
  removeCustomerPlan,
  updateCustomerBusinessName,
  getCustomerBilling,
  reverseInvoice,
  previewAccountDeletion,
  deleteAccount,
  type AccountDeletionPreview,
  SUBSCRIPTION_STATE_LABELS,
  shownPlan,
  type CustomerDetail,
  type CustomerAdminAuditEntry,
  type CustomerBilling,
  type CustomerInvoice,
  type SubscriptionState,
} from '../../lib/customers-api';
import { usePassthroughZone } from '../../components/ZoneContext';
import { InstantTime, formatDate } from '../../lib/time-format';
// #21 — the ONE mapper for staff-API failures. Never render err.message directly:
// it carries the status, the path and the response body.
import { actionErrorToast } from '../../lib/friendly-error';
import { ActionIcon } from '../../components/ActionIcon';

/**
 * /admin/customers/:accountId — the SINGLE customer console (ADR-090).
 *
 * Consolidated from the retired /staff/customers surface. Tabbed sections
 * (Account | Subscription | Domains | Admin audit) with:
 *   - Real subscription-state transitions (SubscriptionStateModal → the real
 *     PUT /admin/subscriptions endpoint; state change + domain cascade). The
 *     ADR-037 audit-only override is RETIRED — every transition appends to the
 *     admin audit log automatically (server-side).
 *   - Add Credit (AddCreditModal, budget-enforced, ADR-086).
 *   - Edit business name (inline).
 *   - Remove plan (the single audited plan action; reviewNotes). REMOVAL ONLY —
 *     staff may clear a plan, never assign one (12 August 2026). See schemas.ts's
 *     AdminTierOverrideBody for why, and for why the route key still says
 *     'tier-override'.
 *   - Admin actions audit log tab.
 *
 * ⛔ NO "LAUNCH ONBOARDING" BUTTON — removed 13 August 2026, and deliberately not
 * replaced here. It minted an ADR-023 §9 impersonation token and opened the customer's
 * OBW in the subscriber portal in a new tab. ADR-076's BOW/SOW/POW quick-action cards on
 * the staff dashboard (`routes/Home.tsx`) superseded it by mounting the OBW engine in
 * place as a modal, with their own launch tokens and atomic provisioning. Admin-assisted
 * onboarding starts from the dashboard cards, NOT from a customer's detail page.
 *
 * Role gating (ADR-012, server-authoritative): destructive transitions +
 * plan removal + name edits are nexa_admin / super_admin. `support` may reactivate
 * (* → active) — the UI mirrors that existing server gate; it does NOT widen it.
 */

type TabId = 'account' | 'subscription' | 'billing' | 'domains' | 'audit';

const INVOICE_STATUS_LABELS: Record<CustomerInvoice['status'], string> = {
  paid: 'Paid',
  pending: 'Pending',
  failed: 'Failed',
  void: 'Void',
  reversed: 'Reversed',
};

const money = (n: number) => `$${n.toFixed(2)}`;

export function AdminCustomerDetailPage() {
  const { accountId } = useParams<{ accountId: string }>();
  /**
   * ADR-120 P8 step 7(b) — which zone this customer lives in, carried on the link from the
   * Customers list. Absent means the staff user's preferred zone, else the home zone (ADR-123
   * Decision 3) — the pre-multi-zone behaviour when no preference is set. The navigation bar
   * states which zone the record came from ("Record from …").
   */
  const [searchParams] = useSearchParams();
  const zone = usePassthroughZone(searchParams.get('zone'));
  const navigate = useNavigate();
  const me = useStaffMe();
  const toast = useToast();

  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [auditLog, setAuditLog] = useState<CustomerAdminAuditEntry[] | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState<TabId>('account');

  // Subscription transition modal (real transition; opened pre-targeted).
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subInitialTarget, setSubInitialTarget] = useState<SubscriptionState | undefined>(undefined);

  // Add Credit modal.
  const [creditOpen, setCreditOpen] = useState(false);

  // Billing tab (PL38 — invoice reversal).
  const [billing, setBilling] = useState<CustomerBilling | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [reverseFor, setReverseFor] = useState<CustomerInvoice | null>(null);
  const [reverseReason, setReverseReason] = useState('');
  const [reverseBusy, setReverseBusy] = useState(false);

  // Business-name edit.
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameBusy, setNameBusy] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  // Plan removal (the single audited plan action — removal only, 12 Aug 2026).
  const [tierNotes, setTierNotes] = useState('');
  const [tierConfirm, setTierConfirm] = useState(false);
  const [tierBusy, setTierBusy] = useState(false);
  const [tierError, setTierError] = useState<string | null>(null);

  // ── Delete account (ADR-079 §2) ──
  // The modal opens on the PREVIEW call, so the impact list and the blocked state come from
  // the server rather than from anything the client guesses.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePreview, setDeletePreview] = useState<AccountDeletionPreview | null>(null);
  const [deletePreviewBusy, setDeletePreviewBusy] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /** B45 — the staffer's tick on the live-snippet warning. Reset every time the modal opens,
   *  so an acknowledgement can never carry over to a different account. */
  const [deleteAckSnippets, setDeleteAckSnippets] = useState(false);

  const reload = useCallback(async () => {
    if (!accountId) return;
    setLoadState('loading');
    try {
      const data = await getCustomer(accountId, zone);
      setDetail(data);
      setNameValue(data.businessName);
      setLoadState('loaded');
    } catch (err) {
      // The page has nothing else to show, so the failure is the page — but it says the same four
      // things a toast does: what (load), where (the zone), why, and a reference.
      const t = actionErrorToast(err, { action: 'Load customer', subject: accountId, zoneLabel: zone.label });
      setLoadError(`${t.title}. ${t.detail}`);
      setLoadState('error');
    }
  }, [accountId, zone]);

  const loadAudit = useCallback(async () => {
    if (!accountId) return;
    try {
      setAuditLog(await listCustomerAdminAudit(accountId, zone));
    } catch {
      setAuditLog([]);
    }
  }, [accountId, zone]);

  const loadBilling = useCallback(async () => {
    if (!accountId) return;
    setBillingLoading(true);
    try {
      setBilling(await getCustomerBilling(accountId, zone));
    } catch (err) {
      toast.push({
        variant: 'error',
        ...actionErrorToast(err, { action: 'Load billing', subject: detail?.businessName ?? accountId, zoneLabel: zone.label }),
      });
      setBilling({ billingRoot: accountId, creditBalance: 0, invoices: [], credits: [] });
    } finally {
      setBillingLoading(false);
    }
  }, [accountId, zone, toast, detail?.businessName]);

  useEffect(() => { void reload(); }, [reload]);

  useEffect(() => {
    if (activeTab === 'audit' && auditLog === null) void loadAudit();
  }, [activeTab, auditLog, loadAudit]);

  useEffect(() => {
    if (activeTab === 'billing' && billing === null && !billingLoading) void loadBilling();
  }, [activeTab, billing, billingLoading, loadBilling]);

  if (me.status === 'loading' || loadState === 'loading') {
    return (
      <main className="portal-page">
        <p className="portal-muted">Loading…</p>
      </main>
    );
  }
  if (me.status === 'error' || loadState === 'error') {
    return (
      <main className="portal-page">
        <h1>Customer</h1>
        <div className="portal-error" role="alert">
          {me.status === 'error' ? me.message : loadError}
        </div>
      </main>
    );
  }

  if (!detail) return null;

  const callerRole = me.me.role;
  const canOverride = isMutating(callerRole as Parameters<typeof isMutating>[0]);

  // Allowed subscription transitions (mirrors ADR-013 table).
  const TRANSITIONS: Record<SubscriptionState, SubscriptionState[]> = {
    active: ['past_due', 'suspended', 'cancelled'],
    past_due: ['active', 'suspended', 'cancelled'],
    suspended: ['active', 'cancelled'],
    cancelled: [],
  };
  const allowedTransitions = TRANSITIONS[detail.subscriptionState] ?? [];

  // Who may drive each control. Subscription transitions are gated to the
  // MUTATING tier (super_admin / nexa_admin) — this matches the LIVE Cedar
  // authorization model (ADR-075 staff cutover), which was confirmed by live
  // probe to deny `support` ALL subscription transitions (including reactivate).
  // The pre-Cedar ADR-012/013 "support may reactivate" per-action tier is NOT
  // granted by the deployed policy; the UI must not offer a control the server
  // rejects (ADR-012 constraint). See the PR note — whether Cedar should grant
  // support a reactivate capability is a separate decision, not widened here.
  const canReactivate = allowedTransitions.includes('active') && canOverride;
  const canDeactivate = allowedTransitions.includes('suspended') && canOverride;
  const canCancel = allowedTransitions.includes('cancelled') && canOverride;
  /**
   * ADR-013 Am.1 (6 October 2026) — domains that did not follow the account's state: still not
   * archived on a cancelled account, still active on a suspended one. Non-zero only when a cascade
   * failed after the state change landed (every staff cancel did, until this fix — Arcana's domain
   * stayed live). The modal's "Finish" repeats the change so the cascade re-runs.
   */
  const domainsBehind =
    detail.subscriptionState === 'cancelled'
      ? detail.domains.filter((d) => d.status !== 'archived').length
      : detail.subscriptionState === 'suspended'
        ? detail.domains.filter((d) => d.status === 'active').length
        : 0;
  const canFinish = canOverride && domainsBehind > 0;
  /** Every action on this page names its target the same way — business · zone. */
  const failToast = (err: unknown, action: string) =>
    toast.push({
      variant: 'error',
      ...actionErrorToast(err, { action, subject: detail.businessName, zoneLabel: zone.label }),
    });
  /**
   * This account is an operator/management ORG (Beta Programme, Nexa Consultants, a
   * `partner-*` node) rather than a customer — ADR-112 D4's `operatorPortalType`.
   *
   * ⚠ THIS PAGE IS THE REASON THE GUARD IS NEEDED, NOT A DUPLICATE OF PR #440'S FILTER.
   * That PR removed operator orgs from the Customers LIST. This page never lists — it
   * point-reads by `accountId` — so the filter cannot reach it, and every route in that
   * does not pass through the list (a bookmark, a pasted URL, the staff node picker,
   * which still shows operator orgs deliberately) still lands here with the full action
   * set rendered. Jeff got here via the list's View button on 12 August 2026 and came
   * within a typed confirmation of deleting both programme orgs.
   *
   * The server now refuses all three mutations outright (`operator_org_not_deletable`,
   * `operator_org_not_creditable`, `operator_org_not_editable` — the last covering BOTH
   * writers of `plan`: the audited tier-override and `PUT /admin/customers/{accountId}`).
   * Hiding them here is the ADR-012 courtesy — never offer a control the server will
   * reject — NOT the safety boundary itself.
   *
   * ⚠ THE BUSINESS-NAME EDIT IS DELIBERATELY STILL OFFERED. The invariant is that an
   * operator org holds no customer plan/tier, not that it is frozen; the server's
   * refusal is scoped to the `plan` field for the same reason.
   */
  const isOperatorOrg = !!detail.operatorPortalType;
  /** B45 — the domains the delete would silence. `?? []` covers a server that predates the
   *  field; the server-side gate is the control either way. */
  const liveSnippetDomains = deletePreview?.liveSnippetDomains ?? [];

  const openSubModal = (target?: SubscriptionState) => {
    setSubInitialTarget(target);
    setSubModalOpen(true);
  };

  const openDeleteModal = async () => {
    if (!detail) return;
    setDeleteOpen(true);
    setDeleteConfirmText('');
    setDeleteError(null);
    setDeleteAckSnippets(false);
    setDeletePreview(null);
    setDeletePreviewBusy(true);
    try {
      setDeletePreview(await previewAccountDeletion(detail.accountId, zone));
    } catch (err) {
      // The impact list is the whole point of the modal — if we cannot compute it, say so
      // rather than offering a delete button over an unknown blast radius.
      // D10 — an API failure is a toast; the delete stays disabled with no preview to act on.
      failToast(err, 'Load the deletion impact');
    } finally {
      setDeletePreviewBusy(false);
    }
  };

  const runDeleteAccount = async () => {
    if (!detail) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const res = await deleteAccount(
        detail.accountId, deleteConfirmText.trim(), deleteAckSnippets || undefined, zone,
      );
      toast.push({
        variant: 'success',
        title: `Account deleted — ${detail.businessName}`,
        detail:
          `${res.domainsDeleted} domain${res.domainsDeleted === 1 ? '' : 's'} and ` +
          `${res.usersDeleted} user${res.usersDeleted === 1 ? '' : 's'} removed. ` +
          `${res.loginsDeleted} login${res.loginsDeleted === 1 ? '' : 's'} deleted` +
          (res.loginsRetained > 0
            ? `, ${res.loginsRetained} kept (attached to other accounts).`
            : '.'),
      });
      setDeleteOpen(false);
      // The account no longer exists — there is no detail page left to reload.
      navigate('/admin/customers');
    } catch (err) {
      // Every server refusal is a toast naming the action, the account and its zone, the reason
      // and a reference (D10; 6 October 2026). Only the typed-confirmation mismatch — about what
      // the operator typed in THIS dialog — stays inline.
      const raw = err instanceof Error ? err.message : String(err);
      if (raw.includes('confirm_mismatch') || raw.includes('confirm_required')) {
        setDeleteError('That does not match. Type the business name or the account email exactly.');
        return;
      }
      failToast(err, 'Delete account');
      if (raw.includes('live_snippet_domains')) {
        // B45 — a RACE: a domain started reporting between the preview and the submit. Refresh the
        // preview so the newly-live domain is named in the warning block, and clear the tick.
        try {
          setDeletePreview(await previewAccountDeletion(detail.accountId, zone));
        } catch {
          // Leave the existing preview in place — a stale impact list beats a blank modal.
        }
        setDeleteAckSnippets(false);
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  const afterSubOrCredit = async () => {
    setAuditLog(null); // force audit re-fetch — the transition appended to it
    await reload();
  };

  const runPlanRemoval = async () => {
    if (!detail) return;
    if (tierNotes.trim().length < 5) { setTierError('Review notes must be at least 5 characters.'); return; }
    const removed = detail.plan;
    setTierBusy(true);
    setTierError(null);
    try {
      await removeCustomerPlan(detail.accountId, tierNotes.trim(), zone);
      toast.push({
        variant: 'success',
        title: `Plan removed — ${removed}`,
        detail: 'The account now has no plan recorded. Audit entry written.',
      });
      setTierConfirm(false);
      setTierNotes('');
      setAuditLog(null);
      await reload();
    } catch (err) {
      failToast(err, 'Remove plan');
    } finally {
      setTierBusy(false);
    }
  };

  const saveName = async () => {
    if (!detail) return;
    const next = nameValue.trim();
    if (next.length < 1) { setNameError('Business name is required.'); return; }
    if (next === detail.businessName) { setEditingName(false); return; }
    setNameBusy(true);
    setNameError(null);
    try {
      await updateCustomerBusinessName(detail.accountId, next, zone);
      toast.push({ variant: 'success', title: 'Business name updated', detail: next });
      setEditingName(false);
      await reload();
    } catch (err) {
      // A toast, not an inline repeat of it (the inline slot is for the field's own validation).
      failToast(err, 'Rename the business');
    } finally {
      setNameBusy(false);
    }
  };

  const runReverse = async () => {
    if (!detail || !reverseFor) return;
    setReverseBusy(true);
    try {
      await reverseInvoice(detail.accountId, reverseFor.invoiceId, reverseReason.trim(), zone);
      toast.push({
        variant: 'success',
        title: `Invoice ${reverseFor.invoiceId} reversed`,
        detail: `A credit note for ${money(reverseFor.total)} was issued.`,
      });
      setReverseFor(null);
      setReverseReason('');
      await loadBilling();
    } catch (err) {
      failToast(err, `Reverse invoice ${reverseFor.invoiceId}`);
    } finally {
      setReverseBusy(false);
    }
  };

  return (
    <main className="portal-page portal-customers-detail">
      <DetailPageHeader backTo="/admin/customers" backLabel="All customers" />

      <header className="portal-partners-header">
        <div>
          <h1>{detail.businessName}</h1>
          <p className="portal-partners-subtitle">
            {detail.customerNumber && (
              <>CN: <code>{detail.customerNumber}</code> &nbsp;·&nbsp; </>
            )}
            {/* D9 (Jeff, 5 October 2026: "it needs to be crystal clear in what zone you are
                deleting the account from") — the account's zone, region-first, beside its number.
                Every action on this page is sent to this zone. */}
            <span className="portal-customers-zone" data-testid="customer-zone">
              Zone: <strong>{zone.label}</strong>
            </span>
            &nbsp;·&nbsp; 
            <span className={`portal-badge portal-badge-${detail.subscriptionState}`}>
              {SUBSCRIPTION_STATE_LABELS[detail.subscriptionState]}
            </span>
          </p>
        </div>
      </header>

      {/* A staffer who arrives here by URL or from the node picker sees a page that looks
          exactly like a customer's. Say why the actions are missing — an unexplained gap
          reads as a bug and invites someone to go looking for another way to do it. */}
      {isOperatorOrg && (
        <p className="portal-operator-org-notice" role="status">
          <strong>This is a platform organisation, not a customer.</strong> {detail.businessName} is
          the {detail.operatorPortalType} programme’s organisation record — the account that
          beta users, consultants and partners are attached to. It has no subscription and no
          domains of its own, so it cannot be credited, deleted, or put on a plan. Its
          name can still be edited.
        </p>
      )}

      <nav className="portal-partners-tabs" aria-label="Customer sections">
        {(['account', 'subscription', 'billing', 'domains', 'audit'] as TabId[]).map((tab) => (
          <button
            key={tab}
            type="button"
            className={`portal-partners-tab${activeTab === tab ? ' active' : ''}`}
            aria-current={activeTab === tab ? 'page' : undefined}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'account' && 'Account'}
            {tab === 'subscription' && 'Subscription'}
            {tab === 'billing' && 'Billing'}
            {tab === 'domains' && `Domains (${detail.domains.length})`}
            {tab === 'audit' && 'Admin audit'}
          </button>
        ))}
      </nav>

      {/* ── Account tab ── */}
      {activeTab === 'account' && (
        <section className="portal-card">
          <header className="portal-card-header">
            <h2>Account</h2>
            {canOverride && (
              <div className="portal-customers-sub-actions">
                {/* Add Credit, Change plan / tier and Delete are all withheld for an
                    operator org — it has no subscription to credit, no plan to hold, and
                    is the structural home other accounts attach to. All three are refused
                    server-side too; see `isOperatorOrg`. */}
                {!isOperatorOrg && (
                  <button type="button" className="portal-btn" onClick={() => setCreditOpen(true)}>
                    Add Credit
                  </button>
                )}
                {/* "Remove plan", not "Change plan / tier" — assignment was withdrawn
                    on 12 August 2026 and the server's body schema no longer accepts a
                    tier. Hidden when there is no plan to remove: the action would be a
                    no-op (the server answers it idempotently), and offering it invites
                    a staffer to hunt for the assign counterpart that no longer exists. */}
                {!isOperatorOrg && detail.plan !== '' && (
                  <button type="button" className="portal-btn" onClick={() => setTierConfirm(true)}>
                    Remove plan
                  </button>
                )}
                {/* ADR-079 §2 — permanent cascade delete. Server-gated on Cedar
                    `accounts:hard_delete`; `canOverride` mirrors that tier here so the UI
                    never offers a control the server would reject (the ADR-012 constraint). */}
                {!isOperatorOrg && (
                  <button
                    type="button"
                    className="portal-btn portal-button-danger"
                    onClick={() => void openDeleteModal()}
                  >
                    Delete account
                  </button>
                )}
              </div>
            )}
          </header>
          <dl className="portal-users-detail-meta">
            <div>
              <dt>Business name</dt>
              <dd>
                {editingName ? (
                  <span className="portal-customers-inline-edit">
                    <input
                      className="portal-input"
                      value={nameValue}
                      onChange={(e) => { setNameValue(e.target.value); setNameError(null); }}
                      disabled={nameBusy}
                      aria-label="Business name"
                    />
                    <button type="button" className="portal-btn" disabled={nameBusy} onClick={() => void saveName()}>
                      {nameBusy ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="portal-link-button-secondary"
                      disabled={nameBusy}
                      onClick={() => { setEditingName(false); setNameValue(detail.businessName); setNameError(null); }}
                    >
                      Cancel
                    </button>
                    {nameError && <span className="portal-field-error">{nameError}</span>}
                  </span>
                ) : (
                  <>
                    {detail.businessName}
                    {canOverride && (
                      <button
                        type="button"
                        className="portal-link-button"
                        style={{ marginLeft: 8 }}
                        onClick={() => { setEditingName(true); setNameValue(detail.businessName); }}
                      >
                        Edit
                      </button>
                    )}
                  </>
                )}
              </dd>
            </div>
            <div><dt>Customer number</dt><dd><code>{detail.customerNumber || '—'}</code></dd></div>
            <div><dt>Email</dt><dd>{detail.email}</dd></div>
            <div><dt>Plan</dt><dd>{shownPlan(detail) || '—'}</dd></div>
            <div><dt>Created</dt><dd><InstantTime iso={detail.createdAt} mode="datetime" /></dd></div>
            <div><dt>Created by</dt><dd><code>{detail.createdBy}</code></dd></div>
          </dl>
        </section>
      )}

      {/* ── Subscription tab ── */}
      {activeTab === 'subscription' && (
        <section className="portal-card">
          <header className="portal-card-header">
            <h2>Subscription</h2>
            {/* Activate / Deactivate / Cancel — the REAL transition (ADR-090).
                Each opens SubscriptionStateModal pre-targeted (state change +
                domain cascade + audit-append). support may reactivate only. */}
            {(canReactivate || canDeactivate || canCancel || canFinish) && (
              <div className="portal-customers-sub-actions">
                {canFinish && (
                  <button
                    type="button"
                    className={detail.subscriptionState === 'cancelled' ? 'portal-btn portal-button-danger' : 'portal-btn'}
                    onClick={() => openSubModal()}
                  >
                    {detail.subscriptionState === 'cancelled' ? 'Finish cancellation' : 'Finish suspension'}
                  </button>
                )}
                {canReactivate && (
                  <button type="button" className="portal-button-primary" onClick={() => openSubModal('active')}>
                    Reactivate
                  </button>
                )}
                {canDeactivate && (
                  <button type="button" className="portal-btn" onClick={() => openSubModal('suspended')}>
                    Deactivate
                  </button>
                )}
                {canCancel && (
                  <button type="button" className="portal-btn portal-button-danger" onClick={() => openSubModal('cancelled')}>
                    Cancel subscription
                  </button>
                )}
              </div>
            )}
          </header>
          <dl className="portal-users-detail-meta">
            <div>
              <dt>State</dt>
              <dd>
                <span className={`portal-badge portal-badge-${detail.subscriptionState}`}>
                  {SUBSCRIPTION_STATE_LABELS[detail.subscriptionState]}
                </span>
              </dd>
            </div>
            <div><dt>Changed at</dt><dd><InstantTime iso={detail.subscriptionStateChangedAt} /></dd></div>
            <div>
              <dt>Plan expiry</dt>
              <dd>{detail.planExpiry ? formatDate(detail.planExpiry) : '—'}</dd>
            </div>
            {detail.subscriptionStateReason && (
              <div><dt>Reason / notes</dt><dd>{detail.subscriptionStateReason}</dd></div>
            )}
          </dl>

          <h3>History (last 10)</h3>
          {detail.subscriptionHistory.length === 0 ? (
            <p className="portal-muted">No state changes recorded yet.</p>
          ) : (
            <ul className="portal-users-audit-list">
              {[...detail.subscriptionHistory].reverse().map((h, i) => (
                <li key={`${h.ts}-${i}`}>
                  <span className="portal-users-audit-action">
                    <code>{h.fromState}</code> → <code>{h.toState}</code>
                  </span>
                  {h.reason && <span className="portal-users-audit-detail"> — {h.reason}</span>}
                  <span className="portal-users-audit-meta">
                    {' '}by {h.staffId} · <InstantTime iso={h.ts} mode="datetime" empty="" />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ── Billing tab (PL38 — invoice ledger + reversal) ── */}
      {activeTab === 'billing' && (
        <section className="portal-card">
          <header className="portal-card-header">
            <h2>Billing</h2>
            {billing && (
              <span className="portal-muted">Credit balance: <strong>{money(billing.creditBalance)}</strong></span>
            )}
          </header>
          <p className="portal-muted">
            Issued invoices are immutable (ADR-083). Reversing an invoice issues an offsetting
            credit note for the full amount — the invoice is marked Reversed and the customer&apos;s
            balance is credited. Both appear in the subscriber&apos;s billing history.
          </p>
          {billing === null || billingLoading ? (
            <p className="portal-muted">Loading…</p>
          ) : billing.invoices.length === 0 ? (
            <p className="portal-muted">No invoices for this customer yet.</p>
          ) : (
            <table className="portal-users-table">
              <thead>
                <tr>
                  <th scope="col">Invoice</th>
                  <th scope="col">Date</th>
                  <th scope="col">Description</th>
                  <th scope="col">Total</th>
                  <th scope="col">Status</th>
                  {canOverride && <th scope="col" className="portal-actions-col">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {billing.invoices.map((inv) => (
                  <tr key={inv.invoiceId}>
                    <td><code>{inv.invoiceId}</code></td>
                    <td>{inv.createdAt ? formatDate(inv.createdAt) : '—'}</td>
                    <td>{inv.description}</td>
                    <td>{money(inv.total)}</td>
                    <td>
                      <span className={`portal-badge portal-badge-${inv.status === 'reversed' ? 'suspended' : inv.status === 'paid' ? 'active' : 'past_due'}`}>
                        {INVOICE_STATUS_LABELS[inv.status]}
                      </span>
                    </td>
                    {canOverride && (
                      <td className="portal-users-row-actions portal-actions-col">
                        {inv.status === 'reversed' ? (
                          <span className="portal-muted">Reversed</span>
                        ) : (
                          <ActionIcon act="pause" icon="undo" label="Reverse invoice"
                            tip="Reverse this invoice — asks for a reason first"
                            onClick={() => { setReverseFor(inv); setReverseReason(''); }} />
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {billing && billing.credits.length > 0 && (
            <>
              <h3>Credit notes &amp; credits</h3>
              <ul className="portal-users-audit-list">
                {billing.credits.map((c) => (
                  <li key={c.entryId}>
                    <span className="portal-users-audit-action">{money(c.total)}</span>
                    <span className="portal-users-audit-detail">
                      {' '}· {c.source}{c.appliedToInvoiceId ? ` · reverses ${c.appliedToInvoiceId}` : ''} — {c.reason}
                    </span>
                    <span className="portal-users-audit-meta">
                      {' '}<InstantTime iso={c.createdAt} mode="datetime" empty="" />
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {/* ── Domains tab ── */}
      {activeTab === 'domains' && (
        <section className="portal-card">
          <h2>Domains ({detail.domains.length})</h2>
          {detail.domains.length === 0 ? (
            <p className="portal-muted">No domains registered for this customer.</p>
          ) : (
            <table className="portal-users-table">
              <thead>
                <tr>
                  <th scope="col">Hostname</th>
                  <th scope="col">Token</th>
                  <th scope="col">Status</th>
                  <th scope="col">Sent data</th>
                  <th scope="col">Created</th>
                </tr>
              </thead>
              <tbody>
                {detail.domains.map((d) => (
                  <tr key={d.domainToken}>
                    <td>
                      {/* ADR-122 Pattern 2 — THE ZONE HAS TO PROPAGATE THROUGH THIS LINK, not
                          just into this page. ADR-122 lists AdminCustomerDetailPage under "no
                          change" because its own reads are already correct, and they are. But
                          the chain Track 2 depends on is Customers → HERE → DomainDetailPage,
                          and this was the step that dropped the zone: a correctly-zoned US
                          customer page handed off a zone-less domain link, so the domain page
                          fell back to the home zone and 404'd on a domain the operator was
                          looking at. One parameter, without which Track 2 cannot work at all. */}
                      <Link to={`/admin/customers/${encodeURIComponent(detail.accountId)}/domains/${encodeURIComponent(d.domainToken)}${zone ? `?zone=${zone.code}` : ''}`}>
                        {d.hostname}
                      </Link>
                    </td>
                    <td><code>{d.domainToken.slice(0, 8)}…</code></td>
                    <td><span className={`portal-badge portal-badge-${d.status}`}>{d.status}</span></td>
                    {/* D5 — has it ever sent data? Read-only; "Unknown" when the check could not answer. */}
                    <td>{d.everSentData === 'yes' ? 'Yes' : d.everSentData === 'no' ? 'Not yet' : 'Unknown'}</td>
                    <td>{d.createdAt ? formatDate(d.createdAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* ── Admin audit tab ── */}
      {activeTab === 'audit' && (
        <section className="portal-card">
          <h2>Admin actions audit log</h2>
          <p className="portal-muted">
            Every admin-tier action on this customer — subscription state transitions, plan / tier
            changes, and credits — with the operator&apos;s notes and identity.
          </p>
          {auditLog === null ? (
            <p className="portal-muted">Loading…</p>
          ) : auditLog.length === 0 ? (
            <p className="portal-muted">No admin actions recorded yet.</p>
          ) : (
            <ul className="portal-users-audit-list">
              {auditLog.map((entry, i) => (
                <li key={`${entry.at}-${i}`}>
                  <span className="portal-users-audit-action"><code>{entry.action}</code></span>
                  <span className="portal-users-audit-detail"> — {entry.reviewNotes}</span>
                  <span className="portal-users-audit-meta">
                    {' '}by {entry.performedByStaffId} · <InstantTime iso={entry.at} mode="datetime" empty="" />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ── Subscription transition modal (real transition, ADR-090) ── */}
      {subModalOpen && (
        <SubscriptionStateModal
          accountId={detail.accountId}
          businessName={detail.businessName}
          currentState={detail.subscriptionState}
          role={callerRole}
          initialTarget={subInitialTarget}
          zone={zone.code}
          domainsBehind={domainsBehind}
          onClose={() => { setSubModalOpen(false); setSubInitialTarget(undefined); }}
          onUpdated={afterSubOrCredit}
        />
      )}

      {/* ── Reverse invoice confirm modal (PL38) ── */}
      {reverseFor && (
        <ActionConfirmModal
          open
          hideClose
          title={`Reverse invoice ${reverseFor.invoiceId} in ${zone.label}`}
          intent="danger"
          primaryLabel={reverseBusy ? 'Reversing…' : 'Reverse invoice'}
          loading={reverseBusy}
          onConfirm={() => void runReverse()}
          onCancel={() => { setReverseFor(null); setReverseReason(''); }}
        >
          <p>
            Issue an offsetting credit note for <strong>{money(reverseFor.total)}</strong> (incl. GST)
            and mark <code>{reverseFor.invoiceId}</code> as <strong>Reversed</strong>. The invoice is
            not edited or deleted — the customer&apos;s balance is credited the full amount. This is a
            correction and is not drawn against your goodwill credit budget.
          </p>
          <label className="portal-users-field">
            Reason (optional — recorded on the credit note)
            <textarea
              className="portal-input"
              rows={3}
              value={reverseReason}
              onChange={(e) => setReverseReason(e.target.value)}
              placeholder="e.g. Billed in error — duplicate charge"
            />
          </label>
        </ActionConfirmModal>
      )}

      {/* ── Add Credit modal (ADR-086) ── */}
      {creditOpen && (
        <AddCreditModal
          zone={zone?.code}
          accountId={detail.accountId}
          businessName={detail.businessName}
          onClose={() => setCreditOpen(false)}
          onApplied={afterSubOrCredit}
        />
      )}

      {/* ── Remove plan confirm modal ──
          There is no plan SELECT here any more, and its absence is the feature: staff
          may clear a plan, never assign one (12 August 2026). The server enforces it —
          the body schema no longer accepts a tier — so this modal cannot express a
          change the API would take. */}
      {tierConfirm && (
        <ActionConfirmModal
          open
          hideClose
          title={`Remove plan in ${zone.label}`}
          intent="warning"
          primaryLabel={tierBusy ? 'Removing…' : 'Remove plan'}
          loading={tierBusy}
          error={tierError}
          onConfirm={() => void runPlanRemoval()}
          onCancel={() => { setTierConfirm(false); setTierError(null); setTierNotes(''); }}
        >
          <p>
            Remove <strong>{detail.plan}</strong> from <strong>{detail.businessName}</strong>.
            They will be left with no plan recorded.
          </p>
          {/* Says plainly what this does NOT do. `Account.plan` is a staff-facing label:
              entitlement resolves from the domain's billing subscription, and the
              customer's own billing screens read the Nexa-Billing record. Letting a
              staffer believe this cancels a subscription is the misread worth pre-empting. */}
          <p className="portal-muted">
            This clears the plan recorded against the account. It does not cancel a
            subscription, change what the customer is billed, or alter what their
            domains can access — use <em>Cancel subscription</em> on the Subscription
            tab for that.
          </p>
          <label className="portal-users-field">
            Review notes (required — 5–500 chars)
            <textarea
              className="portal-input"
              rows={3}
              value={tierNotes}
              onChange={(e) => setTierNotes(e.target.value)}
              placeholder="Reason for removing this plan — recorded in the audit log"
            />
          </label>
        </ActionConfirmModal>
      )}

      {/* ── Delete account confirm modal (ADR-079 §2) ── */}
      {deleteOpen && (
        <ActionConfirmModal
          open
          hideClose
          title={`Delete account in ${zone.label}`}
          intent="danger"
          primaryLabel="Delete permanently"
          loading={deleteBusy}
          error={deleteError}
          // Disabled until the preview has loaded, the gate is clear, and the typed
          // confirmation matches. The server re-checks all three — this is convenience,
          // not the control.
          primaryDisabled={
            deletePreviewBusy ||
            !deletePreview ||
            deletePreview.blocked ||
            deleteConfirmText.trim() === '' ||
            // B45 — live-snippet domains require the explicit tick. The server enforces this
            // too; disabling here just stops the operator hitting a 409 they can see coming.
            (liveSnippetDomains.length > 0 && !deleteAckSnippets)
          }
          onConfirm={() => void runDeleteAccount()}
          onCancel={() => {
            setDeleteOpen(false); setDeleteError(null);
            setDeleteConfirmText(''); setDeleteAckSnippets(false);
          }}
        >
          {deletePreviewBusy && <p className="portal-muted">Loading deletion impact…</p>}

          {deletePreview?.blocked && (
            <p role="alert">
              {deletePreview.blockedReason === 'billing_unavailable' ? (
                <>
                  We could not confirm this account has no active subscription, so it cannot be
                  deleted right now. Please try again shortly.
                </>
              ) : (
                <>
                  <strong>This account has an active subscription.</strong> Cancel the
                  subscription on <strong>{deletePreview.hostname ?? 'its domain'}</strong> first
                  (no refunds), then delete the account — use <em>Cancel subscription</em> on the
                  Subscription tab.
                </>
              )}
            </p>
          )}

          {deletePreview && !deletePreview.blocked && (
            <>
              <p>
                This permanently deletes <strong>{detail.businessName}</strong> and everything
                under it. <strong>This cannot be undone.</strong>
              </p>
              <ul>
                <li>The account record</li>
                <li>
                  {deletePreview.domainCount} domain{deletePreview.domainCount === 1 ? '' : 's'}{' '}
                  and all associated trust-signal data
                </li>
                <li>
                  {deletePreview.users.length} user
                  {deletePreview.users.length === 1 ? '' : 's'} on this account
                </li>
              </ul>

              {deletePreview.users.length > 0 && (
                <>
                  <p>Sign-in logins:</p>
                  <ul>
                    {deletePreview.users.map((u) => (
                      <li key={u.sub}>
                        <strong>{u.email}</strong> —{' '}
                        {u.loginWillBeDeleted
                          ? 'login will be deleted (no other accounts)'
                          : 'login will be kept (attached to another account)'}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* B45 — the live-snippet warning. Named domains, not a count: "1 domain is
                  still reporting" tells a staffer nothing they can act on. */}
              {liveSnippetDomains.length > 0 && (
                <div className="portal-delete-live-snippets" role="alert">
                  <p>
                    <strong>
                      {liveSnippetDomains.length === 1
                        ? 'This site still has the tracking snippet installed.'
                        : 'These sites still have the tracking snippet installed.'}
                    </strong>{' '}
                    Deleting the account stops all reporting for them, and the snippet keeps
                    running on the customer’s site with nowhere to send data.
                  </p>
                  <ul>
                    {liveSnippetDomains.map((d) => (
                      <li key={d.domainToken}>
                        <strong>{d.hostname}</strong>
                        {d.liveness === 'unknown' && (
                          <> — we could not reach the telemetry service, so we cannot confirm
                            whether this one is still live. Treat it as live.</>
                        )}
                      </li>
                    ))}
                  </ul>
                  <label className="portal-users-field portal-delete-live-snippets__ack">
                    <input
                      type="checkbox"
                      checked={deleteAckSnippets}
                      onChange={(e) => setDeleteAckSnippets(e.target.checked)}
                    />
                    <span>
                      I understand{' '}
                      {liveSnippetDomains.length === 1 ? 'this site' : 'these sites'} will stop
                      reporting, and the snippet should be removed from{' '}
                      {liveSnippetDomains.length === 1 ? 'it' : 'them'}.
                    </span>
                  </label>
                </div>
              )}

              <p>A branded closure notice is emailed to {deletePreview.email || 'the account'}.</p>

              <label className="portal-users-field">
                Type <strong>{detail.businessName}</strong> (or the account email) to confirm
                <input
                  className="portal-input"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={detail.businessName}
                  autoComplete="off"
                />
              </label>
            </>
          )}
        </ActionConfirmModal>
      )}

      <footer className="portal-customers-detail-footer">
        <button
          type="button"
          className="portal-customers-detail-close"
          onClick={() => navigate('/admin/customers')}
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Close
        </button>
      </footer>
    </main>
  );
}
